/**
 * Display formatting. Every helper takes null, because most numbers in the
 * contract are optional and "unknown" must never render as 0 — a center whose
 * probe failed reporting "0 studies" is a lie an operator would act on.
 */

const NUMBER = new Intl.NumberFormat(undefined);

/** The dash shown where a value genuinely is not known. */
export const UNKNOWN = '—';

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return UNKNOWN;
  }
  return NUMBER.format(value);
}

export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return UNKNOWN;
  }
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let size = value / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function formatLatency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return UNKNOWN;
  }
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ms`;
}

export function formatMegabytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return UNKNOWN;
  }
  if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`;
  return `${NUMBER.format(value)} MB`;
}

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseDate(value ?? null);
  if (!date) return UNKNOWN;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

export function formatTime(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseDate(value ?? null);
  if (!date) return UNKNOWN;
  return date.toLocaleTimeString(undefined, { timeStyle: 'medium' });
}

/**
 * "12 seconds ago". Used for the fleet's last-checked line, where the absolute
 * timestamp is also shown — this is the part that tells you at a glance that
 * the dashboard has quietly stopped updating.
 */
export function formatRelative(
  value: string | Date | null | undefined,
  now: number = Date.now(),
): string {
  const date = value instanceof Date ? value : parseDate(value ?? null);
  if (!date) return UNKNOWN;

  const seconds = Math.round((now - date.getTime()) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Machine-readable timestamp for a <time datetime="..."> attribute. */
export function isoAttribute(value: string | Date | null | undefined): string | undefined {
  const date = value instanceof Date ? value : parseDate(value ?? null);
  return date ? date.toISOString() : undefined;
}

export function pluralise(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : plural ?? `${singular}s`;
}

/**
 * A span of seconds as a human duration — "3h 23m", "6d 4h".
 *
 * Returns UNKNOWN for null, like every other helper here: a node that does not
 * report its uptime has not been up for zero seconds.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return UNKNOWN;
  }
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}
